import {
  MessageSquare, Bot, Building2, BarChart3,
  CheckCircle, XCircle, AlertCircle, Clock, RefreshCw,
  Save, TestTube, Trash2, Send, PauseCircle, PlayCircle,
  FolderOpen, Settings as SettingsIcon, ChevronRight,
  Wifi, WifiOff, Loader2, FileText, Wrench, HelpCircle
} from 'lucide-react';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import API from '../services/api';
import AIMediaLibrary from './AIMediaLibrary';

function Settings() {
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [orgData, setOrgData] = useState({ name: '', email: '', phone: '' });
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);

  // WhatsApp state
  const [whatsappData, setWhatsappData] = useState({
    phoneNumberId: '',
    accessToken: '',
    businessAccountId: '',
    phoneNumber: '',
    verifyToken: ''
  });
  const [configured, setConfigured] = useState(false);
  const [whatsappActive, setWhatsappActive] = useState(true);
  const [whatsappDeactivationReason, setWhatsappDeactivationReason] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [syncingTemplates, setSyncingTemplates] = useState(false);

  // ✅ New Mode Selector: 'request' (default) vs 'manual'
  const [setupMode, setSetupMode] = useState('request');

  // Setup Request state
  const [setupRequest, setSetupRequest] = useState(null);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [setupFormData, setSetupFormData] = useState({
    businessName: '',
    businessPhone: '',
    businessEmail: '',
    businessAddress: '',
    businessWebsite: '',
    metaBusinessManagerId: '',
    metaAccountEmail: '',
    metaAccountAccess: '',
    businessDescription: '',
    useCase: '',
    expectedVolume: ''
  });

  // AI Agent state
  const [aiAgentData, setAiAgentData] = useState({
    name: 'AI Assistant',
    businessPrompt: '',
    openaiApiKey: '',
    model: 'gpt-4',
    temperature: 0.7,
    maxTokens: 500
  });
  const [aiConfigured, setAiConfigured] = useState(false);
  const [aiActive, setAiActive] = useState(false);
  const [isManaged, setIsManaged] = useState(false);
  const [isCustomSetup, setIsCustomSetup] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  const hasSubscription = localStorage.getItem('subscriptionStatus') === 'active';

  const handleSubscriptionRequired = () => {
    toast.error('Active subscription required! Redirecting to plans...');
    setTimeout(() => window.location.href = '/pricing', 1500);
  };
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingAI, setTestingAI] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadOrganization(),
      loadWhatsAppCredentials(),
      loadAIAgent(),
      loadStats(),
      loadSetupRequest(),
      loadSubscriptionPlan(),
      loadTemplates()
    ]);
    setLoading(false);
  };

  const loadOrganization = async () => {
    try {
      const response = await API.get('/organization');
      setOrgData(response.data.data);
    } catch (error) {
      console.error('Error loading organization:', error);
    }
  };

  const loadWhatsAppCredentials = async () => {
    try {
      const response = await API.get('/organization/whatsapp-credentials');

      if (response.data.configured && response.data.data) {
        setConfigured(true);
        setWhatsappActive(response.data.data.is_active);
        setWhatsappDeactivationReason(
          response.data.data.deactivation_reason || ''
        );

        setWhatsappData({
          phoneNumberId: response.data.data.phone_number_id,
          accessToken: '',
          businessAccountId: response.data.data.business_account_id || '',
          phoneNumber: response.data.data.phone_number || '',
          verifyToken: response.data.data.verify_token || ''
        });
      }
    } catch (error) {
      console.error('Error loading credentials:', error);
    }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await API.get('/templates');
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSyncTemplates = async () => {
    setSyncingTemplates(true);
    const toastId = toast.loading('Syncing templates from Meta...');
    try {
      const response = await API.post('/templates/sync');
      toast.success(response.data.message, { id: toastId });
      await loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to sync templates', { id: toastId });
    } finally {
      setSyncingTemplates(false);
    }
  };

  const templateStatusBadge = (status) => {
    const map = {
      APPROVED: { bg: '#dcfce7', color: '#166534', label: 'Approved' },
      PENDING: { bg: '#fef3c7', color: '#92400e', label: 'In Review' },
      REJECTED: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
    };
    const s = map[status] || map.PENDING;
    return (
      <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', background: s.bg, color: s.color }}>
        {s.label}
      </span>
    );
  };

  const loadSetupRequest = async () => {
    try {
      const response = await API.get('/organization/whatsapp-setup-request');
      if (response.data.hasRequest && response.data.data) {
        setSetupRequest(response.data.data);
        const d = response.data.data;
        setSetupFormData({
          businessName: d.business_name || '',
          businessPhone: d.business_phone || '',
          businessEmail: d.business_email || '',
          businessAddress: d.business_address || '',
          businessWebsite: d.business_website || '',
          metaBusinessManagerId: d.meta_business_manager_id || '',
          metaAccountEmail: d.meta_account_email || '',
          metaAccountAccess: d.meta_account_access || '',
          businessDescription: d.business_description || '',
          useCase: d.use_case || '',
          expectedVolume: d.expected_volume || ''
        });
      }
    } catch (error) {
      console.error('Error loading setup request:', error);
    }
  };

  const loadAIAgent = async () => {
    try {
      const response = await API.get('/organization/ai-agent');

      if (response.data.noSubscription || response.data.planHasAI === false) {
        setAiConfigured(false);
        setAiActive(false);
        setIsManaged(false);
        setAiAgentData({ name: 'AI Assistant', businessPrompt: '', openaiApiKey: '', model: 'gpt-4', temperature: 0.7, maxTokens: 500 });
        return;
      }

      if (!response.data.configured) {
        setAiConfigured(false);
        setAiActive(false);
        setIsManaged(response.data.hasManagedAI || false);
        localStorage.setItem('planHasManagedAI', response.data.hasManagedAI ? 'true' : 'false');
        return;
      }

      if (response.data.configured && response.data.data) {
        setAiConfigured(true);
        setAiActive(!!response.data.data.is_active);
        setIsManaged(response.data.data.is_managed || false);
        localStorage.setItem('planHasManagedAI', response.data.data.is_managed ? 'true' : 'false');
        setAiAgentData({
          name: response.data.data.name,
          businessPrompt: response.data.data.business_prompt,
          openaiApiKey: '',
          model: response.data.data.model,
          temperature: response.data.data.temperature,
          maxTokens: response.data.data.max_tokens
        });
      }
    } catch (error) {
      console.error('Error loading AI agent:', error);
      setAiConfigured(false);
      setAiActive(false);
      setIsManaged(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await API.get('/organization/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadSubscriptionPlan = async () => {
    try {
      const response = await API.get('/subscriptions/my-subscription');
      if (response.data.data) {
        const planName = response.data.data.plan_name || response.data.data.name || '';
        localStorage.setItem('subscriptionPlan', planName);

        const customSetupPlans = [
          'Flow Only - Full Setup',
          'AI + Flow with Credits',
          'flow-only-full',
          'ai-flow-credits',
          'ai-flow-managed-yearly'
        ];
        const isCustom = customSetupPlans.some(p => planName.includes(p) || p.includes(planName));
        setIsCustomSetup(isCustom);
      }
    } catch (error) {
      console.error('Error loading subscription plan:', error);
      setIsCustomSetup(false);
    }
  };

  const handleOrgUpdate = async (e) => {
    e.preventDefault();
    const toastId = toast.loading('Updating organization...');
    try {
      await API.put('/organization', orgData);
      toast.success('Organization updated successfully!', { id: toastId });
    } catch (error) {
      toast.error('Failed to update organization', { id: toastId });
    }
  };

  const handleWhatsAppSave = async (e) => {
    e.preventDefault();
    const toastId = toast.loading('Saving credentials...');
    try {
      await API.post('/organization/whatsapp-credentials', whatsappData);
      toast.success('WhatsApp configured successfully! 🎉', { id: toastId });
      setConfigured(true);
      await loadWhatsAppCredentials();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save credentials';
      if (error.response?.data?.errorCode === 'PHONE_NUMBER_IN_USE') {
        toast.error(
          <div><strong>❌ Already Connected</strong><br />This WhatsApp number is already in use by another organization.</div>,
          { id: toastId, duration: 6000 }
        );
      } else {
        toast.error(errorMessage, { id: toastId });
      }
    }
  };

  const handleRemoveConfiguration = async () => {
    if (!window.confirm('⚠️ Are you sure you want to remove WhatsApp configuration?')) return;
    const toastId = toast.loading('Removing configuration...');
    try {
      await API.delete('/organization/whatsapp-credentials');
      toast.success('WhatsApp configuration removed!', { id: toastId });
      setConfigured(false);
      setWhatsappData({ phoneNumberId: '', accessToken: '', businessAccountId: '', phoneNumber: '', verifyToken: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove configuration', { id: toastId });
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    const toastId = toast.loading('Testing connection...');
    try {
      const response = await API.post('/organization/whatsapp-credentials/test');
      toast.success(
        <div><strong>✅ Connection Successful!</strong><br />{response.data.data.phoneNumber}</div>,
        { id: toastId, duration: 5000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.message || 'Connection failed', { id: toastId });
    }
    setTestingConnection(false);
  };

  const handleSetupRequestSubmit = async (e) => {
    e.preventDefault();
    setSubmittingRequest(true);
    const toastId = toast.loading('Submitting setup request...');
    try {
      await API.post('/organization/whatsapp-setup-request', setupFormData);
      toast.success(
        '✅ Setup request submitted! Our team will contact you within 24-48 hours.',
        { id: toastId, duration: 6000 }
      );
      await loadSetupRequest();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit request', { id: toastId });
    }
    setSubmittingRequest(false);
  };

  const handleCancelSetupRequest = async () => {
    if (!window.confirm('⚠️ Cancel the setup request?')) return;
    const toastId = toast.loading('Cancelling request...');
    try {
      await API.delete('/organization/whatsapp-setup-request');
      toast.success('Setup request cancelled.', { id: toastId });
      setSetupRequest(null);
      setSetupFormData({
        businessName: '', businessPhone: '', businessEmail: '', businessAddress: '',
        businessWebsite: '', metaBusinessManagerId: '', metaAccountEmail: '',
        metaAccountAccess: '', businessDescription: '', useCase: '', expectedVolume: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel', { id: toastId });
    }
  };

  const handleAISave = async (e) => {
    e.preventDefault();
    const toastId = toast.loading('Saving AI Agent...');
    try {
      await API.post('/organization/ai-agent', aiAgentData);
      toast.success('AI Agent configured successfully! 🤖', { id: toastId });
      setAiConfigured(true);
      loadAIAgent();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save AI Agent', { id: toastId });
    }
  };

  const handleTestAI = async () => {
    setTestingAI(true);
    const toastId = toast.loading('Testing AI connection...');
    try {
      const response = await API.post('/organization/ai-agent/test', { model: aiAgentData.model });
      toast.success(
        <div><strong>✅ AI Connected!</strong><br />Test: {response.data.data.test_response?.substring(0, 50)}...</div>,
        { id: toastId, duration: 5000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.message || 'AI test failed', { id: toastId });
    }
    setTestingAI(false);
  };

  const handleToggleAI = async () => {
    const newState = !aiActive;
    const toastId = toast.loading(`${newState ? 'Activating' : 'Deactivating'} AI Agent...`);
    try {
      await API.patch('/organization/ai-agent/toggle', { is_active: newState });
      setAiActive(newState);
      toast.success(`AI Agent ${newState ? 'activated' : 'deactivated'}!`, { id: toastId });
    } catch (error) {
      toast.error('Failed to toggle AI Agent', { id: toastId });
    }
  };

  const handleDeleteAI = async () => {
    if (!window.confirm('⚠️ Delete AI Agent? This will remove all configuration and conversation history.')) return;
    const toastId = toast.loading('Deleting AI Agent...');
    try {
      await API.delete('/organization/ai-agent');
      toast.success('AI Agent deleted!', { id: toastId });
      setAiConfigured(false);
      setAiActive(false);
      setAiAgentData({ name: 'AI Assistant', businessPrompt: '', openaiApiKey: '', model: isManaged ? 'gpt-4' : 'gpt-3.5-turbo', temperature: 0.7, maxTokens: 500 });
    } catch (error) {
      toast.error('Failed to delete AI Agent', { id: toastId });
    }
  };

  const getSetupStatusBadge = (status) => {
    const map = {
      pending: { bg: '#fef3c7', color: '#92400e', icon: <Clock size={13} />, label: 'Pending Review' },
      in_progress: { bg: '#dbeafe', color: '#1e40af', icon: <RefreshCw size={13} />, label: 'In Progress' },
      completed: { bg: '#dcfce7', color: '#166534', icon: <CheckCircle size={13} />, label: 'Completed' },
      rejected: { bg: '#fee2e2', color: '#991b1b', icon: <XCircle size={13} />, label: 'Rejected' }
    };
    const s = map[status] || map.pending;
    return (
      <span style={{ padding: '6px 14px', background: s.bg, color: s.color, borderRadius: '20px', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {s.icon}{s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner">⏳</div>
          <p className="loading-text">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Styles for smooth flip/slide transition */}
      <style>{`
        .wa-setup-switcher {
          display: flex;
          background: #f1f5f9;
          padding: 6px;
          border-radius: 12px;
          gap: 6px;
          margin: 0 auto 28px auto; /* 👈 isse dono button middle/center me aa jayenge */
          width: fit-content;
          border: 1px solid #e2e8f0;
        }
        .wa-switch-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 13.5px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          color: #475569;
          background: transparent;
        }
        .wa-switch-btn-active {
          background: #1e3a5f;
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(30, 58, 95, 0.15);
        }
        .wa-animated-panel {
          animation: waPanelFlip 0.35s cubic-bezier(0.16, 1, 0.3, 1);
          transform-origin: top center;
        }
        @keyframes waPanelFlip {
          from {
            opacity: 0;
            transform: perspective(600px) rotateX(-8deg) translateY(-8px);
          }
          to {
            opacity: 1;
            transform: perspective(600px) rotateX(0deg) translateY(0);
          }
        }
        .wa-help-note {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 12px;
          padding: 16px 18px;
          margin-top: 24px;
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }
      `}</style>

      <div className="settings-wrapper">

        {/* Header */}
        <div className="page-header">
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <SettingsIcon size={28} /> Settings
          </h1>
          <p className="page-subtitle">Manage your organization, WhatsApp, and AI Agent configuration</p>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`settings-tab-btn ${activeTab === 'whatsapp' ? 'settings-tab-btn-active settings-tab-btn-whatsapp-active' : 'settings-tab-btn-inactive'}`}
          >
            <MessageSquare size={16} />
            WhatsApp
            {configured && (
              <span className={`settings-tab-badge ${activeTab === 'whatsapp' ? 'settings-tab-badge-configured' : 'settings-tab-badge-active'}`}>
                CONFIGURED
              </span>
            )}
            {!configured && setupRequest && setupRequest.status === 'pending' && (
              <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: '#fef3c7', color: '#92400e', fontWeight: '700' }}>
                PENDING
              </span>
            )}
            {!configured && setupRequest && setupRequest.status === 'in_progress' && (
              <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: '#dbeafe', color: '#1e40af', fontWeight: '700' }}>
                IN PROGRESS
              </span>
            )}
          </button>

          {/* AI Tab */}
          {(() => {
            const subscriptionPlan = localStorage.getItem('subscriptionPlan') || '';
            const planSupportsAI = !subscriptionPlan.includes('Flow Only');
            if (!planSupportsAI && !isSuperAdmin) return null;
            return (
              <button
                onClick={() => setActiveTab('ai-agent')}
                className={`settings-tab-btn ${activeTab === 'ai-agent' ? 'settings-tab-btn-active settings-tab-btn-ai-active' : 'settings-tab-btn-inactive'}`}
              >
                <Bot size={16} />
                AI Agent
                {aiConfigured && (
                  <span className={`settings-tab-badge ${activeTab === 'ai-agent' ? 'settings-tab-badge-configured' : (aiActive ? 'settings-tab-badge-active' : 'settings-tab-badge-inactive')}`}>
                    {aiActive ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                )}
              </button>
            );
          })()}
        </div>

        {/* ================================================ */}
        {/* WHATSAPP TAB                                     */}
        {/* ================================================ */}
        {activeTab === 'whatsapp' && (
          <div className="settings-tab-content">

            {/* No subscription warning */}
            {!isSuperAdmin && !hasSubscription && (
              <div style={{ padding: '16px', background: '#fef2f2', border: '2px solid #fecaca', borderRadius: '10px', marginBottom: '20px', textAlign: 'center' }}>
                <p style={{ color: '#991b1b', fontWeight: '700', margin: '0 0 8px 0', fontSize: '16px' }}>🔒 WhatsApp Integration Locked</p>
                <p style={{ color: '#b91c1c', margin: '0 0 12px 0', fontSize: '14px' }}>Subscribe to connect your WhatsApp Business account</p>
                <button onClick={() => window.location.href = '/pricing'} style={{ padding: '10px 24px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '15px' }}>
                  View Plans & Subscribe →
                </button>
              </div>
            )}

            {/* ---------------------------------------------------- */}
            {/* CASE 1: Already configured — show current credentials */}
            {/* ---------------------------------------------------- */}
            {configured && (
              <>
                <div className="settings-tab-header">
                  <h2 className="settings-tab-title">WhatsApp Configuration</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span className="settings-status-badge-configured" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <CheckCircle size={13} /> CONFIGURED
                    </span>
                    <span style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: 'default',
                      userSelect: 'none',
                      background: whatsappActive ? '#dcfce7' : '#fee2e2',
                      color: whatsappActive ? '#166534' : '#991b1b',
                    }}>
                      {whatsappActive
                        ? <><Wifi size={13} /> Active</>
                        : <><WifiOff size={13} /> Deactivated</>}
                    </span>
                  </div>
                </div>

                {!whatsappActive && (
                  <div style={{
                    padding: '12px 16px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    marginBottom: '16px',
                  }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#991b1b', fontWeight: '500' }}>
                      ⚠️ Your WhatsApp integration is currently deactivated.
                      {whatsappDeactivationReason ? ` Reason: ${whatsappDeactivationReason}.` : ''}
                      {' '}Please subscribe to a plan to restore WhatsApp functionality.
                    </p>
                    {!isSuperAdmin && (
                      <button
                        onClick={() => window.location.href = '/pricing'}
                        style={{ marginTop: '10px', padding: '8px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}
                      >
                        View Plans →
                      </button>
                    )}
                  </div>
                )}
                <p className="settings-tab-subtitle">
                  {whatsappActive ? 'Your WhatsApp is connected and ready to use.' : 'Your credentials are saved. Reactivate by subscribing to a plan.'}
                </p>

                <form onSubmit={handleWhatsAppSave}>
                  <div className="settings-form-group">
                    <label className="settings-form-label">Phone Number ID <span className="settings-form-required">*</span></label>
                    <input type="text" value={whatsappData.phoneNumberId} onChange={(e) => setWhatsappData({ ...whatsappData, phoneNumberId: e.target.value })} placeholder="182327138287021" className="settings-form-input" required />
                  </div>
                  <div className="settings-form-group">
                    <label className="settings-form-label">Access Token</label>
                    <input type="password" value={whatsappData.accessToken} onChange={(e) => setWhatsappData({ ...whatsappData, accessToken: e.target.value })} placeholder="Enter new token to update" className="settings-form-input" />
                  </div>
                  <div className="settings-form-group">
                    <label className="settings-form-label">Business Account ID</label>
                    <input type="text" value={whatsappData.businessAccountId} onChange={(e) => setWhatsappData({ ...whatsappData, businessAccountId: e.target.value })} placeholder="Business Account ID" className="settings-form-input" />
                  </div>
                  <div className="settings-form-group">
                    <label className="settings-form-label">Phone Number</label>
                    <input type="text" value={whatsappData.phoneNumber} onChange={(e) => setWhatsappData({ ...whatsappData, phoneNumber: e.target.value })} placeholder="+919876543210" className="settings-form-input" />
                  </div>
                  <div className="settings-form-group">
                    <label className="settings-form-label">Webhook Verify Token <span className="settings-form-required">*</span></label>
                    <input type="text" value={whatsappData.verifyToken} onChange={(e) => setWhatsappData({ ...whatsappData, verifyToken: e.target.value })} placeholder="my_secret_webhook_token" className="settings-form-input" required />
                  </div>

                  <div className="settings-actions">
                    <button type="submit" className="settings-btn-save">
                      <Save size={15} /> Update Credentials
                    </button>

                    <button
                      type="button"
                      onClick={handleTestConnection}
                      disabled={testingConnection}
                      className="settings-btn-test"
                    >
                      {testingConnection ? (
                        <>
                          <Loader2 size={15} /> Testing...
                        </>
                      ) : (
                        <>
                          <TestTube size={15} /> Test Connection
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleRemoveConfiguration}
                      className="settings-btn-remove"
                    >
                      <Trash2 size={15} /> Remove Config
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* ---------------------------------------------------- */}
            {/* CASE 2: NOT Configured → Toggle between Request vs Manual */}
            {/* ---------------------------------------------------- */}
            {!configured && (
              <>
                {/* 🔘 TOP ANIMATED SWITCHER BUTTONS */}
                <div className="wa-setup-switcher">
                  <button
                    type="button"
                    onClick={() => setSetupMode('request')}
                    className={`wa-switch-btn ${setupMode === 'request' ? 'wa-switch-btn-active' : ''}`}
                  >
                    <Send size={15} /> 📩 Setup Request (By Team)
                  </button>

                  <button
                    type="button"
                    onClick={() => setSetupMode('manual')}
                    className={`wa-switch-btn ${setupMode === 'manual' ? 'wa-switch-btn-active' : ''}`}
                  >
                    <Wrench size={15} /> ⚙️ Setup Manually
                  </button>
                </div>

                {/* ------------------------------------------------ */}
                {/* VIEW A: SETUP REQUEST (Managed Form)             */}
                {/* ------------------------------------------------ */}
                {setupMode === 'request' && (
                  <div className="wa-animated-panel">
                    <div className="settings-tab-header">
                      <h2 className="settings-tab-title">WhatsApp Setup Request</h2>
                      {setupRequest && getSetupStatusBadge(setupRequest.status)}
                    </div>
                    <p className="settings-tab-subtitle">
                      Don't have Meta API credentials yet? Submit your business details below — our technical team will set up and configure your official WhatsApp Business account within 24-48 hours.
                    </p>

                    {/* Alerts */}
                    {setupRequest && setupRequest.status === 'completed' && (
                      <div className="setup-request-alert setup-request-alert-completed">
                        <p className="setup-request-alert-title">✅ Setup Completed!</p>
                        <p className="setup-request-alert-text">Our team has configured your WhatsApp. Please refresh the page to see your connected account.</p>
                        <button onClick={() => window.location.reload()} className="setup-request-refresh-btn">🔄 Refresh Page</button>
                      </div>
                    )}

                    {setupRequest && setupRequest.status === 'rejected' && (
                      <div className="setup-request-alert setup-request-alert-rejected">
                        <p className="setup-request-alert-title">❌ Request Rejected</p>
                        {setupRequest.admin_notes && (
                          <p className="setup-request-alert-text"><strong>Reason:</strong> {setupRequest.admin_notes}</p>
                        )}
                        <p className="setup-request-alert-text">Please update your details below and resubmit.</p>
                      </div>
                    )}

                    {setupRequest && setupRequest.status === 'in_progress' && (
                      <div className="setup-request-alert setup-request-alert-inprogress">
                        <p className="setup-request-alert-title">🔄 Setup In Progress</p>
                        <p className="setup-request-alert-text">Our team is actively working on your WhatsApp setup. You will be notified once it's ready.</p>
                      </div>
                    )}

                    {setupRequest && setupRequest.status === 'pending' && (
                      <div className="setup-request-alert setup-request-alert-pending">
                        <p className="setup-request-alert-title">⏳ Request Submitted</p>
                        <p className="setup-request-alert-text">Your request is under review. Our team will contact you within 24-48 hours. You can update details and resubmit below.</p>
                      </div>
                    )}

                    {(!setupRequest || setupRequest.status === 'pending' || setupRequest.status === 'rejected') && (
                      <form onSubmit={handleSetupRequestSubmit} className="setup-request-form">
                        <div className="setup-request-section-title">📋 Business Details</div>

                        <div className="setup-request-grid-2">
                          <div className="settings-form-group">
                            <label className="settings-form-label">Business Name <span className="settings-form-required">*</span></label>
                            <input type="text" value={setupFormData.businessName} onChange={(e) => setSetupFormData({ ...setupFormData, businessName: e.target.value })} placeholder="Your Business Name" className="settings-form-input" required />
                          </div>
                          <div className="settings-form-group">
                            <label className="settings-form-label">Business Phone <span className="settings-form-required">*</span></label>
                            <input type="text" value={setupFormData.businessPhone} onChange={(e) => setSetupFormData({ ...setupFormData, businessPhone: e.target.value })} placeholder="+91 9876543210" className="settings-form-input" required />
                          </div>
                        </div>

                        <div className="setup-request-grid-2">
                          <div className="settings-form-group">
                            <label className="settings-form-label">Business Email <span className="settings-form-required">*</span></label>
                            <input type="email" value={setupFormData.businessEmail} onChange={(e) => setSetupFormData({ ...setupFormData, businessEmail: e.target.value })} placeholder="business@example.com" className="settings-form-input" required />
                          </div>
                          <div className="settings-form-group">
                            <label className="settings-form-label">Business Website</label>
                            <input type="text" value={setupFormData.businessWebsite} onChange={(e) => setSetupFormData({ ...setupFormData, businessWebsite: e.target.value })} placeholder="https://yourbusiness.com" className="settings-form-input" />
                          </div>
                        </div>

                        <div className="settings-form-group">
                          <label className="settings-form-label">Business Address</label>
                          <input type="text" value={setupFormData.businessAddress} onChange={(e) => setSetupFormData({ ...setupFormData, businessAddress: e.target.value })} placeholder="123 Main Street, City, State - 000000" className="settings-form-input" />
                        </div>

                        <div className="setup-request-section-title" style={{ marginTop: '28px' }}>🔗 Meta Account Details <span style={{ fontSize: '12px', fontWeight: '400', color: '#94a3b8' }}>(optional)</span></div>

                        <div className="setup-request-grid-2">
                          <div className="settings-form-group">
                            <label className="settings-form-label">Meta Business Manager ID</label>
                            <input type="text" value={setupFormData.metaBusinessManagerId} onChange={(e) => setSetupFormData({ ...setupFormData, metaBusinessManagerId: e.target.value })} placeholder="123456789" className="settings-form-input" />
                          </div>
                          <div className="settings-form-group">
                            <label className="settings-form-label">Meta Account Email</label>
                            <input type="email" value={setupFormData.metaAccountEmail} onChange={(e) => setSetupFormData({ ...setupFormData, metaAccountEmail: e.target.value })} placeholder="meta@example.com" className="settings-form-input" />
                          </div>
                        </div>

                        <div className="settings-form-group">
                          <label className="settings-form-label">Meta Account Access Level</label>
                          <select value={setupFormData.metaAccountAccess} onChange={(e) => setSetupFormData({ ...setupFormData, metaAccountAccess: e.target.value })} className="settings-form-input">
                            <option value="">Select access level...</option>
                            <option value="Admin">Admin</option>
                            <option value="Developer">Developer</option>
                            <option value="Standard">Standard</option>
                            <option value="None">I don't have an account yet</option>
                          </select>
                        </div>

                        <div className="setup-request-section-title" style={{ marginTop: '28px' }}>📊 Usage Details</div>

                        <div className="settings-form-group">
                          <label className="settings-form-label">Business Description</label>
                          <textarea value={setupFormData.businessDescription} onChange={(e) => setSetupFormData({ ...setupFormData, businessDescription: e.target.value })} placeholder="Briefly describe your business — what you sell, who your customers are, etc." rows="3" className="settings-form-textarea" />
                        </div>

                        <div className="setup-request-grid-2">
                          <div className="settings-form-group">
                            <label className="settings-form-label">Primary Use Case</label>
                            <select value={setupFormData.useCase} onChange={(e) => setSetupFormData({ ...setupFormData, useCase: e.target.value })} className="settings-form-input">
                              <option value="">Select use case...</option>
                              <option value="Customer Support">Customer Support</option>
                              <option value="Order Updates">Order Updates / Notifications</option>
                              <option value="Sales & Marketing">Sales & Marketing</option>
                              <option value="Appointment Booking">Appointment Booking</option>
                              <option value="Lead Generation">Lead Generation</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div className="settings-form-group">
                            <label className="settings-form-label">Expected Message Volume</label>
                            <select value={setupFormData.expectedVolume} onChange={(e) => setSetupFormData({ ...setupFormData, expectedVolume: e.target.value })} className="settings-form-input">
                              <option value="">Select volume...</option>
                              <option value="less-than-100">Less than 100/day</option>
                              <option value="100-500">100–500/day</option>
                              <option value="500-1000">500–1000/day</option>
                              <option value="1000-plus">1000+/day</option>
                            </select>
                          </div>
                        </div>

                        <div className="settings-actions" style={{ marginTop: '28px' }}>
                          <button type="submit" disabled={submittingRequest} className="settings-btn-save" style={{ opacity: submittingRequest ? 0.6 : 1 }}>
                            {submittingRequest
                              ? <><Loader2 size={15} /> Submitting...</>
                              : setupRequest
                                ? <><RefreshCw size={15} /> Resubmit Request</>
                                : <><Send size={15} /> Submit Setup Request</>}
                          </button>
                          {setupRequest && setupRequest.status === 'pending' && (
                            <button type="button" onClick={handleCancelSetupRequest} className="settings-btn-remove">
                              <XCircle size={15} /> Cancel Request
                            </button>
                          )}
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {/* ------------------------------------------------ */}
                {/* VIEW B: SETUP MANUALLY (Self Credentials Form)   */}
                {/* ------------------------------------------------ */}
                {setupMode === 'manual' && (
                  <div className="wa-animated-panel">
                    <div className="settings-tab-header">
                      <h2 className="settings-tab-title">⚙️ Configure WhatsApp Credentials</h2>
                    </div>
                    <p className="settings-tab-subtitle">
                      Enter your official Meta WhatsApp Business Cloud API credentials to activate WhatsApp instantly.
                    </p>

                    <form onSubmit={handleWhatsAppSave}>
                      <div className="settings-form-group">
                        <label className="settings-form-label">Phone Number ID <span className="settings-form-required">*</span></label>
                        <input
                          type="text"
                          value={whatsappData.phoneNumberId}
                          onChange={(e) => setWhatsappData({ ...whatsappData, phoneNumberId: e.target.value })}
                          placeholder="e.g. 104829104857291"
                          className="settings-form-input"
                          disabled={!isSuperAdmin && !hasSubscription}
                          required
                        />
                      </div>

                      <div className="settings-form-group">
                        <label className="settings-form-label">Access Token <span className="settings-form-required">*</span></label>
                        <input
                          type="password"
                          value={whatsappData.accessToken}
                          onChange={(e) => setWhatsappData({ ...whatsappData, accessToken: e.target.value })}
                          placeholder="Enter permanent system user access token (EAAB...)"
                          className="settings-form-input"
                          disabled={!isSuperAdmin && !hasSubscription}
                          required
                        />
                      </div>

                      <div className="settings-form-group">
                        <label className="settings-form-label">Business Account ID <span className="settings-form-required">*</span></label>
                        <input
                          type="text"
                          value={whatsappData.businessAccountId}
                          onChange={(e) => setWhatsappData({ ...whatsappData, businessAccountId: e.target.value })}
                          placeholder="e.g. 293847192847561"
                          className="settings-form-input"
                          disabled={!isSuperAdmin && !hasSubscription}
                          required
                        />
                      </div>

                      <div className="settings-form-group">
                        <label className="settings-form-label">Phone Number <span className="settings-form-required">*</span></label>
                        <input
                          type="text"
                          value={whatsappData.phoneNumber}
                          onChange={(e) => setWhatsappData({ ...whatsappData, phoneNumber: e.target.value })}
                          placeholder="+1 (555) 019-2834 or +91 9876543210"
                          className="settings-form-input"
                          disabled={!isSuperAdmin && !hasSubscription}
                          required
                        />
                      </div>

                      <div className="settings-form-group">
                        <label className="settings-form-label">Webhook Verify Token <span className="settings-form-required">*</span></label>
                        <input
                          type="text"
                          value={whatsappData.verifyToken}
                          onChange={(e) => setWhatsappData({ ...whatsappData, verifyToken: e.target.value })}
                          placeholder="Create a secure custom token (e.g. wa_verify_token_secure)"
                          className="settings-form-input"
                          disabled={!isSuperAdmin && !hasSubscription}
                          required
                        />
                      </div>

                      <div className="settings-actions">
                        <button
                          type={(!isSuperAdmin && !hasSubscription) ? 'button' : 'submit'}
                          onClick={(!isSuperAdmin && !hasSubscription) ? handleSubscriptionRequired : undefined}
                          className="settings-btn-save"
                          disabled={!isSuperAdmin && !hasSubscription}
                          style={{
                            opacity: (!isSuperAdmin && !hasSubscription) ? 0.5 : 1,
                            cursor: (!isSuperAdmin && !hasSubscription) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <Save size={15} /> Save & Configure
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* 💡 PROFESSIONAL GUIDANCE NOTE */}
                <div className="wa-help-note">
                  <HelpCircle size={22} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong style={{ fontSize: '13.5px', color: '#1e3a5f', display: 'block', marginBottom: '4px' }}>
                      Need assistance setting up Meta WhatsApp API?
                    </strong>
                    <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
                      If you don't have a verified Meta Business Manager account or prefer our team to handle the technical onboarding, simply submit your details using the <strong>"Setup Request (By Team)"</strong> tab above. Our specialists will configure and activate your WhatsApp Business account seamlessly.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Message Templates Section */}
            {configured && (
              <div style={{ marginTop: '28px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                <div className="settings-tab-header">
                  <h2 className="settings-tab-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileText size={20} /> Message Templates
                  </h2>
                  <button onClick={handleSyncTemplates} disabled={syncingTemplates} className="settings-btn-test" type="button">
                    {syncingTemplates ? <><Loader2 size={15} /> Syncing...</> : <><RefreshCw size={15} /> Sync from Meta</>}
                  </button>
                </div>
                <p className="settings-tab-subtitle">
                  Templates approved in your Meta Business Manager, used to message contacts outside the 24-hour window.
                </p>

                {loadingTemplates ? (
                  <div className="loading-container" style={{ padding: '20px 0' }}>
                    <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#1e3a5f' }} />
                  </div>
                ) : templates.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon"><FileText size={48} color="#cbd5e1" /></div>
                    <h3 className="empty-state-title">No Templates Synced Yet</h3>
                    <p className="empty-state-description">Create a template in Meta Business Manager, then click "Sync from Meta".</p>
                  </div>
                ) : (
                  <div className="table-container table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Language</th>
                          <th>Category</th>
                          <th>Variables</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {templates.map(t => (
                          <tr key={t.id}>
                            <td>{t.template_name}</td>
                            <td>{t.language_code}</td>
                            <td style={{ textTransform: 'capitalize' }}>{(t.category || '').toLowerCase()}</td>
                            <td>{t.variable_count}</td>
                            <td>{templateStatusBadge(t.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ================================================ */}
        {/* AI AGENT TAB                                     */}
        {/* ================================================ */}
        {activeTab === 'ai-agent' && (
          <div className="settings-tab-content">
            {!isSuperAdmin && !hasSubscription && (
              <div style={{ padding: '16px', background: '#fef2f2', border: '2px solid #fecaca', borderRadius: '10px', marginBottom: '20px', textAlign: 'center' }}>
                <p style={{ color: '#991b1b', fontWeight: '700', margin: '0 0 8px 0', fontSize: '16px' }}>🔒 AI Agent Locked</p>
                <p style={{ color: '#b91c1c', margin: '0 0 12px 0', fontSize: '14px' }}>Subscribe to unlock GPT-4 powered AI assistant</p>
                <button onClick={() => window.location.href = '/pricing'} style={{ padding: '10px 24px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '15px' }}>View Plans & Subscribe →</button>
              </div>
            )}

            {(isSuperAdmin || hasSubscription) && (
              <div style={{
                marginBottom: '28px',
                padding: '24px',
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)',
                borderRadius: '14px',
                border: '2px solid rgba(30, 58, 95, 0.3)',
                boxShadow: '0 4px 16px rgba(30, 58, 95, 0.2)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '280px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                      <FolderOpen size={28} color="#ffffff" />
                      <h3 style={{
                        margin: 0,
                        color: '#ffffff',
                        fontSize: '20px',
                        fontWeight: '700',
                        letterSpacing: '-0.5px'
                      }}>
                        AI Media Library
                      </h3>
                    </div>
                    <p style={{
                      margin: 0,
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '14px',
                      lineHeight: '1.6'
                    }}>
                      Upload images, documents, videos for your AI Agent to send automatically based on keyword triggers.
                      Perfect for product catalogs, FAQs, menus, and more.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowMediaLibrary(true)}
                    style={{
                      padding: '14px 28px',
                      backgroundColor: '#ffffff',
                      color: '#1e3a5f',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                  >
                    <FolderOpen size={18} />
                    Manage Library
                  </button>
                </div>
              </div>
            )}

            <div className="settings-tab-header">
              <h2 className="settings-tab-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Bot size={22} /> AI Agent Configuration
              </h2>
              {aiConfigured && (
                <div className="settings-status-badges">
                  <span className={aiActive ? 'settings-status-badge-active' : 'settings-status-badge-inactive'}>
                    {aiActive
                      ? <><CheckCircle size={13} /> ACTIVE</>
                      : <><AlertCircle size={13} /> INACTIVE</>}
                  </span>
                </div>
              )}
            </div>
            <p className="settings-tab-subtitle">Configure OpenAI-powered AI assistant for smart conversations</p>

            <form onSubmit={handleAISave}>
              <div className="settings-form-group">
                <label className="settings-form-label">Assistant Name</label>
                <input type="text" value={aiAgentData.name} onChange={(e) => setAiAgentData({ ...aiAgentData, name: e.target.value })} placeholder="Customer Support AI" className="form-input" />
              </div>
              <div className="settings-form-group">
                <label className="settings-form-label">Business Prompt <span className="settings-form-required">*</span></label>
                <textarea value={aiAgentData.businessPrompt} onChange={(e) => setAiAgentData({ ...aiAgentData, businessPrompt: e.target.value })} placeholder="You are a helpful customer support assistant for an e-commerce company. Be friendly and professional. Help customers with orders, returns, and general inquiries." rows="6" className="settings-form-textarea" required />
                <p className="settings-form-hint">All models require an active OpenAI billing account with sufficient credits.</p>
              </div>

              {!isManaged && aiConfigured && aiActive && (
                <div className="self-managed-notice">
                  <div className="self-managed-notice-icon">⚠️</div>
                  <div className="self-managed-notice-content">
                    <p className="self-managed-notice-title">Action Required — Flow Mode Conflict</p>
                    <p className="self-managed-notice-text">
                      Your AI Agent is currently <strong>active</strong>. While AI is active, WhatsApp Flow automation will not execute.
                      If you are not using an OpenAI API key or wish to use Flow-based automation, please <strong>deactivate the AI Agent</strong> to restore Flow functionality.
                    </p>
                  </div>
                </div>
              )}

              {!isManaged ? (
                <>
                  <div className="settings-form-group">
                    <label className="settings-form-label">OpenAI API Key <span className="settings-form-required">*</span></label>
                    <input type="password" value={aiAgentData.openaiApiKey} onChange={(e) => setAiAgentData({ ...aiAgentData, openaiApiKey: e.target.value })} placeholder={aiConfigured ? "Enter new key to update" : "sk-proj-..."} className="settings-form-input" required={!aiConfigured} />
                    <p className="settings-form-hint">Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="settings-form-link">OpenAI Platform</a></p>
                  </div>
                  <div className="settings-form-group">
                    <label className="settings-form-label">AI Model</label>
                    <select value={aiAgentData.model} onChange={(e) => setAiAgentData({ ...aiAgentData, model: e.target.value })} className="settings-form-input">
                      <optgroup label="💰 Low Cost">
                        <option value="gpt-3.5-turbo">GPT-3.5 Turbo — Fast & cost effective</option>
                        <option value="gpt-3.5-turbo-16k">GPT-3.5 Turbo 16K — Longer context, low cost</option>
                      </optgroup>
                      <optgroup label="⚡ GPT-4o Series (Recommended)">
                        <option value="gpt-4o-mini">GPT-4o Mini — Best value, very capable</option>
                        <option value="gpt-4o">GPT-4o — Latest flagship, fast & smart</option>
                      </optgroup>
                      <optgroup label="🧠 GPT-4 Series">
                        <option value="gpt-4-turbo">GPT-4 Turbo — High quality, large context</option>
                        <option value="gpt-4">GPT-4 — Classic, requires paid plan</option>
                      </optgroup>
                      <optgroup label="🔬 Advanced / Reasoning">
                        <option value="o1-mini">o1 Mini — Reasoning model, complex tasks</option>
                        <option value="o3-mini">o3 Mini — Latest reasoning, efficient</option>
                      </optgroup>
                    </select>
                    <p className="settings-form-hint">Choose a model based on your OpenAI plan. GPT-3.5 Turbo works on free accounts.</p>
                  </div>
                  <div className="ai-credit-info-box">
                    <div className="ai-credit-info-icon">💡</div>
                    <div className="ai-credit-info-content">
                      <p className="ai-credit-info-title">OpenAI Credits Required for Premium Models</p>
                      <p className="ai-credit-info-text">Advanced models like GPT-4o and GPT-4 Turbo require an active OpenAI billing account with sufficient credits. Free-tier accounts are limited to GPT-3.5 Turbo.</p>
                      <p className="ai-credit-info-upgrade">✨ <strong>Upgrade Tip:</strong> Switch to our <span className="ai-credit-info-plan-link" onClick={() => window.location.href = '/pricing'}>AI + Flow with Credits</span> plan — no API key needed, no billing hassle!</p>
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ padding: '16px', background: '#dcfce7', border: '2px solid #86efac', borderRadius: '10px', marginBottom: '20px' }}>
                  <p style={{ color: '#166534', fontWeight: '700', margin: '0 0 8px 0', fontSize: '16px' }}>✅ Using ChatTech24 AI Credits</p>
                  <p style={{ color: '#15803d', margin: '0', fontSize: '14px' }}>Your plan includes AI credits powered by ChatTech24. No OpenAI API key needed!</p>
                </div>
              )}

              <div className="settings-actions">
                <button
                  type={(!isSuperAdmin && !hasSubscription) ? 'button' : 'submit'}
                  onClick={(!isSuperAdmin && !hasSubscription) ? handleSubscriptionRequired : undefined}
                  className="settings-btn-save"
                >
                  <Save size={15} /> Save AI Agent
                </button>

                {aiConfigured && (
                  <>
                    <button
                      type="button"
                      onClick={handleTestAI}
                      disabled={testingAI}
                      className="settings-btn-test"
                    >
                      {testingAI ? (
                        <>
                          <Loader2 size={15} className="animate-spin" /> Testing...
                        </>
                      ) : (
                        <>
                          <TestTube size={15} /> Test AI
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleToggleAI}
                      className={`settings-btn-toggle ${aiActive
                        ? 'settings-btn-toggle-deactivate'
                        : 'settings-btn-toggle-activate'
                        }`}
                    >
                      {aiActive ? (
                        <>
                          <PauseCircle size={15} /> Deactivate
                        </>
                      ) : (
                        <>
                          <PlayCircle size={15} /> Activate
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleDeleteAI}
                      className="settings-btn-remove"
                    >
                      <Trash2 size={15} /> Delete
                    </button>
                  </>
                )}
              </div>
            </form>

            {aiConfigured && aiActive && (
              <div className="ai-active-alert-modern">
                <div className="ai-active-alert-header">
                  <div className="ai-active-alert-icon-wrap">
                    <span className="ai-active-alert-icon">🤖</span>
                  </div>
                  <div>
                    <h3 className="ai-active-alert-title">AI Agent Successfully Activated</h3>
                    <span className="ai-active-alert-badge">● Live</span>
                  </div>
                </div>

                <p className="ai-active-alert-desc">
                  Your AI Agent is now active and fully operational.
                  {configured
                    ? ' With your WhatsApp integration in place, the assistant is ready to intelligently handle every incoming conversation — delivering context-aware, professional responses in real time.'
                    : ' Once your WhatsApp configuration is complete, the assistant will be fully ready to handle every incoming conversation with intelligent, context-aware responses.'}
                </p>

                <div className="ai-active-alert-cards">
                  <div className="ai-active-alert-card">
                    <span className="ai-active-alert-card-icon">💬</span>
                    <div>
                      <p className="ai-active-alert-card-title">Conversation Handling</p>
                      <p className="ai-active-alert-card-text">All incoming WhatsApp messages are being managed by your AI assistant</p>
                    </div>
                  </div>
                  <div className="ai-active-alert-card">
                    <span className="ai-active-alert-card-icon">🧠</span>
                    <div>
                      <p className="ai-active-alert-card-title">Context Awareness</p>
                      <p className="ai-active-alert-card-text">Responses are generated based on your business prompt and full conversation history</p>
                    </div>
                  </div>
                </div>

                <div className="ai-active-alert-tip">
                  <span>💡</span>
                  <p>To switch back to Flow-based automation, deactivate the AI Agent. Flow mode and AI mode cannot run simultaneously.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Organization Tab */}
        {activeTab === 'organization' && (
          <div className="settings-tab-content">
            <h2 className="settings-tab-title">Organization Details</h2>
            <form onSubmit={handleOrgUpdate}>
              <div className="form-group">
                <label className="form-label">Organization Name</label>
                <input type="text" value={orgData.name} onChange={(e) => setOrgData({ ...orgData, name: e.target.value })} className="form-input" required />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input type="email" value={orgData.email} onChange={(e) => setOrgData({ ...orgData, email: e.target.value })} className="form-input" required />
              </div>
              <button type="submit" className="settings-btn-save" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Save size={15} /> Save Changes
              </button>
            </form>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="settings-tab-content">
            <h2 className="settings-tab-title">Platform Statistics</h2>
            <div className="settings-stats-grid">
              <div className="settings-stat-card settings-stat-card-primary"><div className="settings-stat-label">Total Flows</div><div className="settings-stat-value">{stats.totalFlows}</div></div>
              <div className="settings-stat-card settings-stat-card-pink"><div className="settings-stat-label">Total Contacts</div><div className="settings-stat-value">{stats.totalContacts}</div></div>
              <div className="settings-stat-card settings-stat-card-blue"><div className="settings-stat-label">Total Messages</div><div className="settings-stat-value">{stats.totalMessages}</div></div>
              <div className="settings-stat-card settings-stat-card-green"><div className="settings-stat-label">Today's Messages</div><div className="settings-stat-value">{stats.todayMessages}</div></div>
            </div>
          </div>
        )}

        {/* AI Media Library Modal */}
        {showMediaLibrary && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999,
            padding: '20px',
            backdropFilter: 'blur(4px)',
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              maxWidth: '1400px',
              width: '100%',
              maxHeight: '92vh',
              overflow: 'hidden',
              boxShadow: '0 25px 70px rgba(0, 0, 0, 0.4)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'modalSlideIn 0.3s ease-out',
            }}>
              <div style={{
                padding: '28px 36px',
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '2px solid rgba(255, 255, 255, 0.15)',
                flexShrink: 0,
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                    <FolderOpen size={32} color="#ffffff" />
                    <h2 style={{
                      margin: 0,
                      color: '#ffffff',
                      fontSize: '26px',
                      fontWeight: '800',
                      letterSpacing: '-0.5px'
                    }}>
                      AI Media Library
                    </h2>
                  </div>
                  <p style={{
                    margin: 0,
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Manage files for your AI Agent to send automatically based on keywords
                  </p>
                </div>
                <button
                  onClick={() => setShowMediaLibrary(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    color: '#ffffff',
                    fontSize: '24px',
                    cursor: 'pointer',
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    fontWeight: '300',
                    lineHeight: '1',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                    e.target.style.transform = 'rotate(90deg) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                    e.target.style.transform = 'rotate(0deg) scale(1)';
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                flex: 1,
                overflow: 'auto',
                backgroundColor: '#f8fafc',
              }}>
                <AIMediaLibrary isEmbedded={true} />
              </div>
            </div>

            <style>{`
              @keyframes modalSlideIn {
                from {
                  opacity: 0;
                  transform: scale(0.9) translateY(30px);
                }
                to {
                  opacity: 1;
                  transform: scale(1) translateY(0);
                }
              }
            `}</style>
          </div>
        )}
      </div>
    </div>
  );
}

export default Settings;